import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  BannerNotification,
  DropdownField,
  Heading,
  MessageTemplate,
  rgba,
  SwitchField,
  Text,
  UseFormReturnType,
} from '@frontend/design-system';
import { INITIAL_DIRECT_BOOKING_MESSAGE_TEMPLATE } from '../../constants';
import { DropdownOption } from '../../types';
import { WaitlistComposeMessage } from '../waitlist-compose-message';
import { AppointmentSlots } from './appointment-slots';
import { apptDetailsFormFields, WaitlistSlotAvailabilityStatus } from './use-waitlist-appointment-details-form';

interface Props {
  timezoneAbbr: string;
  form: UseFormReturnType<typeof apptDetailsFormFields, keyof typeof apptDetailsFormFields>;
  providerOptions: DropdownOption[];
  appointmentTypeOptions: DropdownOption[];
  activeSlotCount: number;
  availabilityStatus: WaitlistSlotAvailabilityStatus;
  isDirectBookingEnabled: boolean;
  onSlotCountChange: (count: number) => void;
}

export const WaitlistAppointmentDetails = ({
  timezoneAbbr,
  form,
  providerOptions,
  appointmentTypeOptions,
  activeSlotCount,
  onSlotCountChange,
  availabilityStatus,
  isDirectBookingEnabled,
}: Props) => {
  const { t } = useTranslation('schedule-quick-fill');
  const tokens = useTranslationTokens();

  return (
    <section css={containerStyle}>
      <header css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(3) }}>
        <Icon name='calendar' />
        <Heading level={3}>{t('Appointment Details')}</Heading>
      </header>
      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        <AppointmentSlots
          timezoneAbbr={timezoneAbbr}
          form={form}
          activeSlotCount={activeSlotCount}
          onSlotCountChange={onSlotCountChange}
        />
        {isDirectBookingEnabled && (
          <>
            <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
              <Text weight='bold'>{t('Direct Scheduling')}</Text>
              <Text color='subdued'>
                {t(
                  'When Direct Scheduling is enabled, the {{contact}} will receive a link where they can automatically book the appointment.',
                  { contact: tokens.individualContact.toLowerCase() }
                )}
              </Text>
            </div>
            <SwitchField
              {...form.getFieldProps('directScheduling')}
              name='directScheduling'
              label={t('Enable Direct Scheduling')}
            />
          </>
        )}
        {form.values.directScheduling ? (
          <>
            <DropdownField
              disabled={!form.values.directScheduling}
              {...form.getFieldProps('provider')}
              name='provider'
              label={t('Provider')}
            >
              {providerOptions.map((provider) => (
                <DropdownField.Option key={provider.value} value={provider.value}>
                  {provider.label}
                </DropdownField.Option>
              ))}
            </DropdownField>
            <DropdownField
              disabled={!form.values.directScheduling}
              {...form.getFieldProps('appointmentType')}
              name='appointmentType'
              label={t('Appointment Type')}
            >
              {appointmentTypeOptions.map((appointmentType) => (
                <DropdownField.Option key={appointmentType.value} value={appointmentType.value}>
                  {appointmentType.label}
                </DropdownField.Option>
              ))}
            </DropdownField>

            <AnimatePresence>
              {availabilityStatus !== 'not-required' && (
                <motion.div
                  css={{ overflow: 'hidden', flexShrink: 0 }}
                  initial={{ opacity: 0, height: 0, margin: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0, margin: 0 }}
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                >
                  <AvailabilityBanner status={availabilityStatus} />
                </motion.div>
              )}
            </AnimatePresence>

            <MessageTemplate initialTemplate={INITIAL_DIRECT_BOOKING_MESSAGE_TEMPLATE} tags={[]} draggableTags isNewTag>
              <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                <Text size='small' color='subdued'>
                  {t('Message Preview')}
                </Text>
                <MessageTemplate.Preview
                  css={{ background: rgba(theme.colors.primary50, 0.1), p: { fontSize: theme.font.size.medium } }}
                />
              </div>
            </MessageTemplate>
          </>
        ) : (
          <WaitlistComposeMessage />
        )}
      </div>
    </section>
  );
};

const AvailabilityBanner = ({ status }: { status: WaitlistSlotAvailabilityStatus }) => {
  const { t } = useTranslation('schedule-quick-fill');

  if (status === 'available') {
    return <BannerNotification status='success' message={t('The selected time slots are available for booking.')} />;
  }

  if (status === 'unavailable') {
    return (
      <BannerNotification
        status='error'
        message={t(
          'No availability found for the selected provider, date and time. Please select a different time slot or provider.'
        )}
      />
    );
  }

  if (status === 'checking') {
    return (
      <BannerNotification
        status='info'
        message={t('Checking availability for the selected provider, date and time...')}
      />
    );
  }

  if (status === 'error') {
    return (
      <BannerNotification
        status='error'
        message={t(
          'There was an error checking availability for the selected provider, date and time. Please try again later.'
        )}
      />
    );
  }

  return null;
};

const containerStyle = css({
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.small,
  padding: theme.spacing(3),
  boxShadow: theme.shadows.light,
  maxWidth: 450,
});
